// LearningStyleStep.js - Learning style selection step
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from '../../i18n/useTranslation';

const LEARNING_STYLES = [
  {
    id: 'visual',
    label: 'Visual',
    description: 'I learn best through images, videos, and diagrams',
    icon: 'eye',
    color: '#3b82f6',
  },
  {
    id: 'auditory',
    label: 'Auditory',
    description: 'I learn best by listening and discussing',
    icon: 'ear-hearing',
    color: '#8b5cf6',
  },
  {
    id: 'reading',
    label: 'Reading/Writing',
    description: 'I learn best through reading and taking notes',
    icon: 'book-open-page-variant',
    color: '#10b981',
  },
  {
    id: 'kinesthetic',
    label: 'Hands-on',
    description: 'I learn best by doing and experimenting',
    icon: 'hand-pointing-up',
    color: '#f59e0b',
  },
];

const LearningStyleStep = ({ value = 'visual', onChange, onNext, onBack }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [style, setStyle] = useState(value);

  useEffect(() => {
    setStyle(value);
  }, [value]);

  const colors = {
    bg: isDark ? '#1e293b' : '#f8fafc',
    card: isDark ? '#334155' : '#ffffff',
    text: isDark ? '#f8fafc' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    accent: '#f59e0b',
    border: isDark ? '#475569' : '#e2e8f0',
  };

  const selectStyle = (styleId) => {
    setStyle(styleId);
    onChange?.(styleId);
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.bg }]}>
      <View style={[styles.content, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Icon */}
        <View style={[styles.iconContainer, { backgroundColor: colors.accent + '15' }]}>
          <MaterialCommunityIcons name="brain" size={48} color={colors.accent} />
        </View>

        {/* Title */}
        <Text style={[styles.title, { color: colors.text }]}>
          {t('How do you learn best?')}
        </Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          {t("We'll tailor content to your learning style")}
        </Text>

        {/* Learning Style Options */}
        <View style={styles.stylesContainer}>
          {LEARNING_STYLES.map((ls) => {
            const isSelected = style === ls.id;
            return (
              <TouchableOpacity
                key={ls.id}
                style={[
                  styles.styleCard,
                  {
                    backgroundColor: isSelected ? ls.color + '10' : colors.bg,
                    borderColor: isSelected ? ls.color : colors.border,
                  },
                ]}
                onPress={() => selectStyle(ls.id)}
                activeOpacity={0.7}
              >
                {/* Radio */}
                <View
                  style={[
                    styles.radio,
                    {
                      borderColor: isSelected ? ls.color : colors.border,
                      backgroundColor: isSelected ? ls.color : 'transparent',
                    },
                  ]}
                >
                  {isSelected && <View style={styles.radioInner} />}
                </View>

                {/* Icon */}
                <View
                  style={[
                    styles.styleIcon,
                    { backgroundColor: isSelected ? ls.color + '20' : colors.border + '50' },
                  ]}
                >
                  <MaterialCommunityIcons
                    name={ls.icon}
                    size={24}
                    color={isSelected ? ls.color : colors.textSecondary}
                  />
                </View>

                {/* Text */}
                <View style={styles.styleText}>
                  <Text
                    style={[
                      styles.styleLabel,
                      { color: isSelected ? ls.color : colors.text },
                    ]}
                  >
                    {t(ls.label)}
                  </Text>
                  <Text style={[styles.styleDescription, { color: colors.textSecondary }]}>
                    {t(ls.description)}
                  </Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Navigation Buttons */}
        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.secondaryButton, { borderColor: colors.border }]}
            onPress={onBack}
            activeOpacity={0.7}
          >
            <MaterialCommunityIcons name="arrow-left" size={18} color={colors.text} />
            <Text style={[styles.secondaryButtonText, { color: colors.text }]}>{t('Back')}</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.accent }]}
            onPress={onNext}
            activeOpacity={0.8}
          >
            <Text style={styles.primaryButtonText}>{t('Continue')}</Text>
            <MaterialCommunityIcons name="arrow-right" size={18} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    width: '100%',
    maxWidth: 520,
    borderRadius: 16,
    padding: 32,
    borderWidth: 1,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  iconContainer: {
    width: 88,
    height: 88,
    borderRadius: 44,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 24,
  },
  stylesContainer: {
    width: '100%',
    gap: 10,
    marginBottom: 28,
  },
  styleCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 12,
    borderWidth: 2,
    gap: 12,
  },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#fff',
  },
  styleIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
  },
  styleText: {
    flex: 1,
  },
  styleLabel: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 2,
  },
  styleDescription: {
    fontSize: 12,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    width: '100%',
  },
  secondaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 10,
    borderWidth: 1,
    gap: 6,
  },
  secondaryButtonText: {
    fontSize: 15,
    fontWeight: '500',
  },
  primaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 10,
    gap: 6,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
});

export default LearningStyleStep;
